import { type ReactElement, type ReactNode } from "react";
import { Icon, type IconName } from "./Icon";
import { cn } from "./utils/cn";

export interface NavItem {
  num?: string;
  icon?: IconName;
  label: ReactNode;
  href?: string;
  active?: boolean;
  onClick?: () => void;
  /**
   * Optional right-aligned badge — counts ("18"), money ("$124"), tier ("L3"). Brand-kit
   * `Shell.jsx` uses these on the Queue / Earnings / etc rows. Rendered in mono, fg-muted.
   * Cosmetic only when {@code direction === "vertical"} (sidebar). Top-bar variant ignores.
   */
  count?: string | number;
  badgeTone?: "neutral" | "alert" | "success";
  /**
   * Optional section name — items sharing a section value get grouped under one
   * "§ <SECTION>" header. Per brand-kit ui_kits/platform/Shell.jsx ("§ NAVIGATE", "§ SKILLS").
   * Items with no section render in the default unlabeled group at the top.
   */
  section?: string;
}

export interface NavProps {
  items: NavItem[];
  className?: string;
  /**
   * Layout direction. {@code horizontal} (default) is the original top-bar shape.
   * {@code vertical} stacks items for a sidebar — pair with {@code AppShell}'s {@code sidebar}
   * slot.
   */
  direction?: "horizontal" | "vertical";
  /**
   * Render-prop for the link element. Defaults to a plain {@code <a>}, which causes a full page
   * reload and ignores React Router's basename — fine for cross-SPA links (e.g.
   * {@code href="/admin/sign-in"} from inside the requester SPA), broken for in-SPA navigation
   * inside an app whose router has a basename (e.g. {@code basename="/admin"} bouncing
   * {@code href="/users"} to {@code localhost:8082/users} → 404).
   *
   * <p>To get SPA navigation + basename resolution, callers inject {@code react-router-dom}'s
   * {@code Link}:
   *
   * <pre>{@code
   * <Nav items={items} renderLink={(p) =>
   *   <Link to={p.href} className={p.className}>{p.children}</Link>
   * } />
   * }</pre>
   *
   * <p>{@code @humaniti/ui} stays free of a router dependency this way.
   */
  renderLink?: (props: { href: string; className: string; children: ReactNode }) => ReactElement;
}

/**
 * Render the inner content of a single nav item. The vertical sidebar follows the brand-kit
 * portal mocks: optional icon, label, right-aligned badge. Numbered labels remain supported for
 * horizontal/legacy callers.
 */
const renderInner = (item: NavItem) => (
  <>
    <span className="hui-nav__item-main">
      {item.icon && <Icon name={item.icon} size={16} className="hui-nav__item-icon" />}
      {item.num && <span className="hui-nav__item-num">{item.num}</span>}
      {item.num && <span className="hui-nav__item-sep">/</span>}
      <span className="hui-nav__item-label">{item.label}</span>
    </span>
    {item.count != null && (
      <span
        className={cn(
          "hui-nav__item-count",
          item.badgeTone === "alert" && "hui-nav__item-count--alert",
          item.badgeTone === "success" && "hui-nav__item-count--success"
        )}
      >
        {item.count}
      </span>
    )}
  </>
);

const renderItem = (item: NavItem, i: number, renderLink: NavProps["renderLink"]): ReactElement => {
  const inner = renderInner(item);
  const cls = cn("hui-nav__item", item.active && "hui-nav__item--active");
  if (item.href) {
    if (renderLink) {
      return (
        <span key={i} className="hui-nav__item-wrapper">
          {renderLink({ href: item.href, className: cls, children: inner })}
        </span>
      );
    }
    return (
      <a key={i} className={cls} href={item.href}>
        {inner}
      </a>
    );
  }
  return (
    <button key={i} type="button" className={cls} onClick={item.onClick}>
      {inner}
    </button>
  );
};

export const Nav = ({ items, className, direction = "horizontal", renderLink }: NavProps) => {
  // Group by section — preserves first-seen order so the unlabeled (no section) group always
  // comes first. Empty sections are not rendered. Sections only render when in vertical layout
  // (top-bar nav stays a flat list).
  const grouped: { section: string | null; items: NavItem[] }[] = [];
  for (const item of items) {
    const key = item.section ?? null;
    let bucket = grouped.find((g) => g.section === key);
    if (!bucket) {
      bucket = { section: key, items: [] };
      grouped.push(bucket);
    }
    bucket.items.push(item);
  }

  return (
    <nav className={cn("hui-nav", direction === "vertical" && "hui-nav--vertical", className)}>
      {direction === "vertical" && grouped.length > 0
        ? grouped.map((g, gi) => (
            <div key={gi} className="hui-nav__section">
              {g.section && <div className="hui-nav__section-label">§ {g.section}</div>}
              {g.items.map((item, i) => renderItem(item, i, renderLink))}
            </div>
          ))
        : items.map((item, i) => renderItem(item, i, renderLink))}
    </nav>
  );
};
